import { z } from "zod";

import { CTA_TYPES, EVENT_NAMES, FORM_STATUSES, UTM_KEYS } from "@/lib/analytics-taxonomy";
import { MAX_PIXEL } from "@/lib/section-visibility";

/**
 * Single source of truth for the shape of analytics payloads.
 *
 * Both the browser (before sending a beacon) and the ingest endpoint
 * (`/api/public/events`) validate against these schemas, so field names and
 * types can never drift apart. The taxonomy itself lives in
 * `src/lib/analytics-taxonomy.ts`; the documentation in
 * `docs/analytics-events.md` describes the semantics.
 */

export const utmSchema = z.object(
  Object.fromEntries(UTM_KEYS.map((key) => [key, z.string().max(120).optional()])) as Record<
    (typeof UTM_KEYS)[number],
    z.ZodOptional<z.ZodString>
  >,
);

const pixel = z.number().finite().min(0).max(MAX_PIXEL);

export const analyticsEventSchema = z.object({
  name: z.enum(EVENT_NAMES),
  label: z.string().min(1).max(120),
  page: z.string().max(200),
  target: z.string().max(200).optional(),
  ctaType: z.enum(CTA_TYPES).optional(),
  formId: z.string().max(80).optional(),
  formStatus: z.enum(FORM_STATUSES).optional(),
  detail: z.string().max(120).optional(),
  clickY: pixel.optional(),
  scrollY: pixel.optional(),
  /** Version tag of the target section (e.g. "book-meeting.2026-08"). */
  sectionVersion: z.string().max(40).optional(),
  utm: utmSchema.optional(),
});

export const analyticsPayloadSchema = z.object({
  events: z.array(analyticsEventSchema).min(1).max(20),
});

export type ValidatedAnalyticsEvent = z.infer<typeof analyticsEventSchema>;
export type AnalyticsPayload = z.infer<typeof analyticsPayloadSchema>;
